import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './ProjectPage.css';

function ProjectPage({ project, onBack }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isLoading, setIsLoading] = useState(true);

  const selectedItem = project.images[currentIndex]; // Define selectedItem here

  useEffect(() => {
    setIsLoading(true); // Set loading state to true when the image changes
  
    const image = new Image();
    image.src = selectedItem.url;
    image.onload = () => {
      setIsLoading(false); // Set loading state to false once the image is loaded
    };
    image.onerror = () => {
      setIsLoading(false); // Handle error case if needed
    };
  
    // Cleanup if the component unmounts or the selectedItem changes
    return () => {
      setIsLoading(true); // Reset loading state
    };
  }, [selectedItem]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        handlePrev();
      } else if (e.key === 'ArrowRight') {
        handleNext();
      }
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [currentIndex]);

  const handleNext = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % project.images.length);
  };

  const handlePrev = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? project.images.length - 1 : prevIndex - 1
    );
  };

  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (touchStart - touchEnd > 50) {
      handleNext();
    } else if (touchEnd - touchStart > 50) {
      handlePrev();
    }
  };

  const getEmbedUrl = (url) => {
    if (!url) return null;
    const videoId = url.split('v=')[1];
    return `https://www.youtube.com/embed/${videoId}`;
  };

  const LoadingAnimation = () => {
    const [currentFrame, setCurrentFrame] = useState(0);
    const frames = Array.from({ length: 36 }, (_, i) => `/loading_images/Untitled_Artwork-${i + 1}.png`); // Adjust the number of frames as needed

    useEffect(() => {
      const interval = setInterval(() => {
        setCurrentFrame((prevFrame) => (prevFrame + 1) % frames.length);
      }, 100); // Adjust the frame rate as needed

      return () => clearInterval(interval);
    }, [frames.length]);

    return (
      <div className="loading-animation">
        <img src={frames[currentFrame]} alt="Loading..." />
      </div>
    );
  };

  return (
    <div className="project-page-container">
      <button className="back-button" onClick={onBack}>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M19 12H5"></path>
          <path d="M12 5l-7 7 7 7"></path>
        </svg>
      </button>

      <div className="content-wrapper">
        {/* Gallery Section */}
        <div className="gallery-section">
          <div
            className="main-image-container"
            onTouchStart={isMobile ? handleTouchStart : undefined}
            onTouchMove={isMobile ? handleTouchMove : undefined}
            onTouchEnd={isMobile ? handleTouchEnd : undefined}
          >
            <AnimatePresence mode="wait">
              {isLoading ? (
                <motion.div
                  key="loading"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5 }}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <LoadingAnimation />
                </motion.div>
              ) : (
                <motion.div
                  key={currentIndex}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5 }}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  {selectedItem.type === 'video' ? (
                    <iframe
                      src={getEmbedUrl(selectedItem.url)}
                      title="Project Video"
                      className="main-video"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      style={{ width: '90%', height: '90%', border: 'none' }}
                      allowFullScreen
                    ></iframe>
                  ) : selectedItem.type === 'website' ? (
                    <iframe
                      src={selectedItem.url}
                      title="Website Preview"
                      className="main-website"
                      allowFullScreen
                      style={{ width: '90%', height: '90%', border: 'none' }}
                    ></iframe>
                  ) : (
                    <img
                      src={selectedItem.url}
                      alt={`${project.title} ${currentIndex + 1}`}
                      className="main-image"
                    />
                  )}
                </motion.div>
              )}
            </AnimatePresence>

            <button className="nav-button prev" onClick={handlePrev}>
              &lt;
            </button>
            <button className="nav-button next" onClick={handleNext}>
              &gt;
            </button>
          </div>

          {/* Thumbnails (only for desktop) */}
          {!isMobile && (
            <div className="thumbnail-container">
              {project.images.map((item, index) => (
                <motion.div
                  key={index}
                  className={`thumbnail ${index === currentIndex ? 'active' : ''}`}
                  onClick={() => setCurrentIndex(index)}
                >
                  {item.type === 'video' ? (
                    <div className="video-thumbnail">
                      <img
                        src={`https://img.youtube.com/vi/${item.url.split('v=')[1]}/0.jpg`}
                        alt="Video Thumbnail"
                        className="thumbnail-image"
                      />
                      <span className="play-icon">▶</span>
                    </div>
                  ) : item.type === 'website' ? (
                    <div className="website-thumbnail">
                      <span className="website-icon">🌐</span>
                    </div>
                  ) : (
                    <img
                      src={item.url}
                      alt={`${project.title} ${index + 1}`}
                      className="thumbnail-image"
                    />
                  )}
                </motion.div>
              ))}
            </div>
          )}
        </div>

        {/* Project Details */}
        <div className={`project-details ${isMobile ? 'mobile' : ''}`}>
          <h1>{project.title}</h1>
          <p className="project-date">{project.date}</p>
          <p className="project-summary">{project.summary}</p>
          
          {/* Add the link button if project.link exists */}
          {project.link && (
          <a
            href={project.link}
            target="_blank"
            rel="noopener noreferrer"
            className="project-link-button"
          >
            Visit Project
          </a>
        )}
        </div>
      </div>
    </div>
  );
}

export default ProjectPage;